import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CommissionState } from "../../../types";
import { axiosObj } from "../../../../config/apiClients";

const initialState: CommissionState = {
  isLoading: false,
  isGenerating: false,
  agentcommission: [],
  agentCommissionHistory: [],
  commissionDetails: [],
  agentCommissionHistoryPagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
  },
};

export const getMonthlyCommissions = createAsyncThunk(
  "/sales-agents/commissions/getMonthly",
  async ({ month }: { month: string }, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(
        `/sales-agents/commissions/getMonthly?month=${month}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Fetch Commissions" });
    }
  },
);

export const generateCommission = createAsyncThunk(
  "/sales-agents/commissions/generate",
  async (
    payload: { agentId: string; month: string; commissionPercentage: number; monthEndEquitySnapshot: number; monthTotalNet: number; assignedClientsCount: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.post(
        "/sales-agents/commissions/generate",
        payload,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Generate Commission" });
    }
  },
);

export const finalizeCommission = createAsyncThunk(
  "/sales-agents/commissions/finalize",
  async (commissionId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch(
        `/sales-agents/commissions/${commissionId}/finalize`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Finalize Commission" });
    }
  },
);

export const getAgentCommissionHistory = createAsyncThunk(
  "/sales-agents/commissions/getAgentHistory",
  async (
    { agentId, paramPage, paramLimit }: { agentId: string; paramPage: number; paramLimit: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.get(
        `/sales-agents/${agentId}/commissions/history?paramPage=${paramPage}&paramLimit=${paramLimit}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Fetch Commission History" });
    }
  },
);

export const getAgentCommissionDetail = createAsyncThunk(
  "/sales-agents/agent/commissions",
  async (
    { agentId, month }: { agentId: string; month: string; },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.get(
        `sales-agents/${agentId}/commissions?month=${month}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Fetch Agent Commission Detail" });
    }
  },
);

const commissionSlice = createSlice({
  name: "commission",
  initialState,
  reducers: {
    resetCommissions(state) {
      state.agentcommission = [];
    },
    resetAgentCommissionHistory(state) {
      state.agentCommissionHistory = [];
      state.agentCommissionHistoryPagination = {
        totalItems: 0,
        currentPage: 1,
        totalPages: 1,
        hasNextPage: false,
      };
    },
    updateLocalPercentage(
      state,
      action: { payload: { agentId: string; commissionPercentage: number } },
    ) {
      const idx = state.agentcommission.findIndex(
        (r: any) => r.agentId === action.payload.agentId,
      );
      if (idx !== -1) {
        state.agentcommission[idx].commissionPercentage = action.payload.commissionPercentage;
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getMonthlyCommissions.pending, (state) => { state.isLoading = true; })
      .addCase(getMonthlyCommissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agentcommission = action.payload.payload || [];
      })
      .addCase(getMonthlyCommissions.rejected, (state) => {
        state.isLoading = false;
        state.agentcommission = [];
      })
      .addCase(generateCommission.pending, (state) => { state.isGenerating = true; })
      .addCase(generateCommission.fulfilled, (state, action) => {
        state.isGenerating = false;
        const updated = action.payload.payload.record ?? action.payload.payload;
        const idx = state.agentcommission.findIndex((r: any) => r.agentId === updated.agentId);
        if (idx !== -1) {
          state.agentcommission[idx] = { ...state.agentcommission[idx], ...updated };
        } else {
          state.agentcommission.push(updated);
        }
      })
      .addCase(generateCommission.rejected, (state) => { state.isGenerating = false; })
      .addCase(finalizeCommission.fulfilled, (state, action) => {
        const updated = action.payload.payload.record ?? action.payload.payload;
        const idx = state.agentcommission.findIndex((r: any) => r._id === updated._id);
        if (idx !== -1) {
          state.agentcommission[idx] = { ...state.agentcommission[idx], ...updated };
        }
      })
      .addCase(getAgentCommissionHistory.pending, (state) => { state.isLoading = true; })
      .addCase(getAgentCommissionHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload.payload.commissions;
        state.agentCommissionHistory = payload ?? [];
        state.agentCommissionHistoryPagination = {
          totalItems: payload?.totalItems ?? 0,
          currentPage: payload?.page ?? 1,
          totalPages: payload?.totalPages ?? 1,
          hasNextPage: payload?.hasNextPage ?? false,
        };
      })
      .addCase(getAgentCommissionHistory.rejected, (state) => {
        state.isLoading = false;
        state.commissionDetails = [];
      })
      .addCase(getAgentCommissionDetail.pending, (state) => { state.isLoading = true; })
      .addCase(getAgentCommissionDetail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.commissionDetails = action.payload.payload.commissionDetails;
      })
      .addCase(getAgentCommissionDetail.rejected, (state) => {
        state.isLoading = false;
        state.commissionDetails = [];
      });
  },
});

export const {
  resetCommissions,
  resetAgentCommissionHistory,
  updateLocalPercentage,
} = commissionSlice.actions;

export default commissionSlice.reducer;